import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import type { DetalleFooter, Evento, EventoTema, FooterInvitacion, Ponente } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { TextField } from '../components/TextField';
import { apiAuth, ApiError } from '../lib/apiAuth';
import { useAdminEventoData } from './AdminEventoDataProvider';

/** ISO <-> valor de <input type="datetime-local">. */
function isoAInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}
function inputAIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const TEMA_VACIO: EventoTema = { primario: null, secundario: null, terciario: null };
const PONENTES_VACIOS: Ponente[] = [
  { nombre: null, subtitulo: null, descripcion: null, fotoUrl: null },
  { nombre: null, subtitulo: null, descripcion: null, fotoUrl: null },
  { nombre: null, subtitulo: null, descripcion: null, fotoUrl: null },
];
const FOOTER_VACIO: FooterInvitacion = {
  titulo: null,
  descripcion: null,
  facebookUrl: null,
  instagramUrl: null,
  detalles: [],
};

const ETIQUETA_ESTADO: Record<Evento['estado'], string> = {
  borrador: 'Borrador',
  activo: 'Activo',
  archivado: 'Archivado',
};

export function ConfigScreen() {
  const { eventoId, evento } = useAdminEventoData();
  const [f, setF] = useState<Evento | null>(null);
  const [abre, setAbre] = useState('');
  const [cierra, setCierra] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [nuevaPrioridad, setNuevaPrioridad] = useState('');

  useEffect(() => {
    if (evento && !f) {
      setF(evento);
      setAbre(isoAInput(evento.ventanaRegistro.abre));
      setCierra(isoAInput(evento.ventanaRegistro.cierra));
    }
  }, [evento, f]);

  if (!f) return null;

  function set<K extends keyof Evento>(k: K, v: Evento[K]) {
    setF((prev) => (prev ? { ...prev, [k]: v } : prev));
  }

  function setColor(k: keyof EventoTema, v: string) {
    setF((prev) => (prev ? { ...prev, tema: { ...(prev.tema ?? TEMA_VACIO), [k]: v || null } } : prev));
  }

  function agregarPrioridad() {
    const v = nuevaPrioridad.trim();
    if (!v) return;
    setF((prev) => {
      if (!prev) return prev;
      const actuales = prev.prioridades ?? [];
      if (actuales.some((p) => p.toLowerCase() === v.toLowerCase())) return prev;
      return { ...prev, prioridades: [...actuales, v] };
    });
    setNuevaPrioridad('');
  }

  function quitarPrioridad(p: string) {
    setF((prev) => (prev ? { ...prev, prioridades: (prev.prioridades ?? []).filter((x) => x !== p) } : prev));
  }

  function onPrioridadKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      agregarPrioridad();
    }
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    if (!f) return;
    setError(null);
    setOk(false);
    setGuardando(true);
    try {
      await apiAuth(`/admin/eventos/${eventoId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          nombre: f.nombre,
          fechaTexto: f.fechaTexto,
          horaTexto: f.horaTexto || '',
          fechaInicio: f.fechaInicio,
          lugarTexto: f.lugarTexto,
          mapsUrl: f.mapsUrl,
          flyerUrl: f.flyerUrl || null,
          mensajeTexto1: f.mensajeTexto1 || null,
          mensajeTexto2: f.mensajeTexto2 || null,
          aforoObjetivo: Number(f.aforoObjetivo) || 0,
          ventanaRegistro: { abre: inputAIso(abre), cierra: inputAIso(cierra) },
          tema: f.tema ?? TEMA_VACIO,
          prioridades: f.prioridades ?? [],
          estado: f.estado,
          heroImagenUrl: f.heroImagenUrl || null,
          ponentes: f.ponentes ?? PONENTES_VACIOS,
          videoUrl: f.videoUrl || null,
          mapaEmbedUrl: f.mapaEmbedUrl || null,
          direccionTexto: f.direccionTexto || null,
          telefonoRequerido: f.telefonoRequerido,
          footer: f.footer ?? FOOTER_VACIO,
        }),
      });
      setOk(true);
      setTimeout(() => setOk(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  const tema = f.tema ?? TEMA_VACIO;

  return (
    <form className="max-w-xl space-y-6" onSubmit={guardar}>
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-texto">Estado del evento</h2>
        <p className="text-xs text-texto/50">
          Los eventos empiezan en borrador. Actívalo para que asesores y staff puedan usarlo con normalidad.
        </p>
        <select
          className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
          value={f.estado}
          onChange={(e) => set('estado', e.target.value as Evento['estado'])}
        >
          {(Object.keys(ETIQUETA_ESTADO) as Evento['estado'][]).map((estado) => (
            <option key={estado} value={estado}>
              {ETIQUETA_ESTADO[estado]}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-texto">Datos del evento</h2>
        <TextField label="Nombre" value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
        <TextField
          label="Fecha (texto)"
          placeholder="10 de octubre"
          value={f.fechaTexto}
          onChange={(e) => set('fechaTexto', e.target.value)}
        />
        <TextField
          label="Hora del evento"
          placeholder="7:00 pm"
          value={f.horaTexto ?? ''}
          onChange={(e) => set('horaTexto', e.target.value)}
          hint="Se muestra junto a la fecha y el lugar en la invitación"
        />
        <TextField label="Lugar" value={f.lugarTexto} onChange={(e) => set('lugarTexto', e.target.value)} />
        <TextField
          label="Liga de Google Maps"
          value={f.mapsUrl}
          onChange={(e) => set('mapsUrl', e.target.value)}
          hint="Opcional"
        />
        <TextField
          label="Liga del flyer"
          value={f.flyerUrl ?? ''}
          onChange={(e) => set('flyerUrl', e.target.value || null)}
          hint="Link a la imagen del flyer oficial (súbela a WhatsApp, Drive, etc. y pega aquí el link). El asesor la verá lista para descargar junto al mensaje."
        />
        <TextField
          label="Objetivo de aforo (personas)"
          type="number"
          inputMode="numeric"
          value={String(f.aforoObjetivo)}
          onChange={(e) => set('aforoObjetivo', Number(e.target.value) as Evento['aforoObjetivo'])}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-texto" htmlFor="abre">
              Registro abre
            </label>
            <input
              id="abre"
              type="datetime-local"
              className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
              value={abre}
              onChange={(e) => setAbre(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-texto" htmlFor="cierra">
              Registro cierra
            </label>
            <input
              id="cierra"
              type="datetime-local"
              className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
              value={cierra}
              onChange={(e) => setCierra(e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-texto/50">
          Deja las fechas vacías para mantener el registro siempre abierto.
        </p>
      </div>

      <MensajesInvitacion f={f} set={set} />

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-texto">Prioridades</h2>
        <p className="text-xs text-texto/50">
          Tags que podrás asignar a cada invitado registrado (p.ej. VIP, AAA, AA, A). Agrega los que
          necesite este evento.
        </p>
        <div className="flex flex-wrap gap-2">
          {(f.prioridades ?? []).map((p) => (
            <Chip key={p} activo onClick={() => quitarPrioridad(p)}>
              {p} ×
            </Chip>
          ))}
          {(f.prioridades ?? []).length === 0 && (
            <span className="text-xs text-texto/40">Sin prioridades todavía.</span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
            placeholder="Nueva prioridad (Enter para agregar)"
            value={nuevaPrioridad}
            onChange={(e) => setNuevaPrioridad(e.target.value)}
            onKeyDown={onPrioridadKeyDown}
          />
          <Button type="button" variante="secundario" onClick={agregarPrioridad}>
            Agregar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-texto">Tema del evento</h2>
        <p className="text-xs text-texto/50">
          Hasta 3 colores. Se usan en las páginas públicas de invitación y boleto de este evento.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ColorField etiqueta="Primario" valor={tema.primario} onChange={(v) => setColor('primario', v)} />
          <ColorField etiqueta="Secundario" valor={tema.secundario} onChange={(v) => setColor('secundario', v)} />
          <ColorField etiqueta="Terciario" valor={tema.terciario} onChange={(v) => setColor('terciario', v)} />
        </div>

        <Card
          className="flex items-center gap-3 p-4"
          style={{
            ['--evento-primario' as string]: tema.primario ?? undefined,
            ['--evento-secundario' as string]: tema.secundario ?? undefined,
            ['--evento-terciario' as string]: tema.terciario ?? undefined,
          }}
        >
          <span
            className="rounded-full px-3.5 py-2 text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--evento-primario, var(--color-navy))' }}
          >
            Botón primario
          </span>
          <span
            className="rounded-full px-3.5 py-2 text-xs font-semibold"
            style={{
              backgroundColor: 'var(--evento-terciario, var(--color-fondo))',
              color: 'var(--evento-secundario, var(--color-oro))',
            }}
          >
            Vista previa
          </span>
        </Card>
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-texto">Página de invitación (landing)</h2>
          <p className="mt-1 text-xs text-texto/50">
            Contenido que ven los invitados antes de registrarse. Deja en blanco lo que no quieras
            mostrar: esa sección se oculta sola.
          </p>
        </div>
        <TextField
          label="Imagen del hero"
          value={f.heroImagenUrl ?? ''}
          onChange={(e) => set('heroImagenUrl', e.target.value || null)}
          hint="Imagen destacada junto al nombre del evento (p.ej. portada de un libro). Pega el link de la imagen."
        />
        <TextField
          label="Dirección"
          value={f.direccionTexto ?? ''}
          onChange={(e) => set('direccionTexto', e.target.value || null)}
          hint="Dirección completa (calle, colonia, ciudad) que se muestra en la sección de ubicación, además del nombre del lugar. Vacío = no se muestra."
        />
        <TextField
          label="Video de cómo llegar"
          value={f.videoUrl ?? ''}
          onChange={(e) => set('videoUrl', e.target.value || null)}
          hint="Link directo a un .mp4/.webm, o a YouTube/Vimeo. Se muestra junto al mapa, en la sección de ubicación. Vacío = sin video."
        />
        <TextField
          label="Liga de embed de Google Maps"
          value={f.mapaEmbedUrl ?? ''}
          onChange={(e) => set('mapaEmbedUrl', e.target.value || null)}
          hint='En Google Maps: Compartir → Insertar un mapa → copia solo la URL dentro de src="...", no el HTML completo. Vacío = sin mapa.'
        />
      </div>

      <PonentesConfig f={f} set={set} />

      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-texto">Formulario de registro</h2>
        <label className="flex items-center gap-2 text-sm text-texto">
          <input
            type="checkbox"
            checked={f.telefonoRequerido}
            onChange={(e) => set('telefonoRequerido', e.target.checked)}
          />
          Pedir teléfono como obligatorio
        </label>
      </div>

      <FooterConfig f={f} set={set} />

      {error && <Callout tono="error">{error}</Callout>}
      {ok && <Callout tono="exito">Guardado.</Callout>}
      <Button type="submit" cargando={guardando}>
        Guardar cambios
      </Button>
    </form>
  );
}

const PLACEHOLDERS_MENSAJE = ['{{nombre}}', '{{fecha}}', '{{lugar}}', '{{liga}}'] as const;
const LIGA_EJEMPLO = `${window.location.origin}/i/AbCdEf123456`;

function mensajeDefault1Preview(f: Evento): string {
  const cuando = f.fechaTexto ? `\n📅 ${f.fechaTexto}` : '';
  const donde = f.lugarTexto ? `\n📍 ${f.lugarTexto}` : '';
  return `¡Hola! Te comparto tu invitación para ${f.nombre || 'nuestro evento'}.${cuando}${donde}`;
}

function mensajeDefault2Preview(): string {
  return `Confirma tu asistencia aquí:\n${LIGA_EJEMPLO}\n\nLa liga es personal y de un solo uso.`;
}

function aplicarPlaceholdersPreview(plantilla: string, f: Evento): string {
  return plantilla
    .replaceAll('{{nombre}}', f.nombre || 'nuestro evento')
    .replaceAll('{{fecha}}', f.fechaTexto || '')
    .replaceAll('{{lugar}}', f.lugarTexto || '')
    .replaceAll('{{liga}}', LIGA_EJEMPLO);
}

function MensajesInvitacion({
  f,
  set,
}: {
  f: Evento;
  set: <K extends keyof Evento>(k: K, v: Evento[K]) => void;
}) {
  const previa1 = f.mensajeTexto1 ? aplicarPlaceholdersPreview(f.mensajeTexto1, f) : mensajeDefault1Preview(f);
  const previa2 = f.mensajeTexto2 ? aplicarPlaceholdersPreview(f.mensajeTexto2, f) : mensajeDefault2Preview();

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-texto">Mensajes de invitación</h2>
        <p className="mt-1 text-xs text-texto/50">
          Este es el mensaje que los asesores copiarán y pegarán para invitar (texto 1, imagen del flyer, texto 2).
          Déjalo en blanco para usar el mensaje por defecto. Placeholders disponibles:{' '}
          <code className="rounded bg-navy/5 px-1 font-mono">{PLACEHOLDERS_MENSAJE.join(' ')}</code>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-texto">Mensaje 1 (antes de la imagen)</label>
          <textarea
            className="min-h-28 w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto shadow-sm focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
            value={f.mensajeTexto1 ?? ''}
            onChange={(e) => set('mensajeTexto1', e.target.value || null)}
            placeholder="¡Hola! Te comparto tu invitación..."
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-texto">Mensaje 2 (después de la imagen)</label>
          <textarea
            className="min-h-28 w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto shadow-sm focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
            value={f.mensajeTexto2 ?? ''}
            onChange={(e) => set('mensajeTexto2', e.target.value || null)}
            placeholder="Confirma tu asistencia aquí..."
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-texto/70">Vista previa (lo que verá y copiará el asesor)</p>
        <Card className="space-y-3 bg-slate-50/50 p-3.5 sm:p-4">
          <PreviaMensaje etiqueta="1. Texto" texto={previa1} />
          <PreviaMensaje etiqueta="2. Imagen" texto={f.flyerUrl ? '(se copia el flyer del evento)' : '(sin flyer configurado)'} />
          <PreviaMensaje etiqueta="3. Texto" texto={previa2} />
        </Card>
      </div>
    </div>
  );
}

function PreviaMensaje({ etiqueta, texto }: { etiqueta: string; texto: string }) {
  return (
    <div className="space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-texto/40">{etiqueta}</span>
      <p className="whitespace-pre-wrap rounded-lg bg-white p-2.5 text-sm text-texto shadow-sm">{texto}</p>
    </div>
  );
}

function PonentesConfig({
  f,
  set,
}: {
  f: Evento;
  set: <K extends keyof Evento>(k: K, v: Evento[K]) => void;
}) {
  const ponentes = f.ponentes ?? PONENTES_VACIOS;

  function setPonente(i: number, patch: Partial<Ponente>) {
    const siguiente = ponentes.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    set('ponentes', siguiente);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-texto">Ponentes</h2>
        <p className="mt-1 text-xs text-texto/50">
          Hasta 3. Deja el nombre en blanco para no mostrar ese bloque.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {ponentes.map((p, i) => (
          <Card key={i} className="space-y-3 p-4">
            <TextField
              label={`Ponente ${i + 1} — nombre`}
              value={p.nombre ?? ''}
              onChange={(e) => setPonente(i, { nombre: e.target.value || null })}
            />
            <TextField
              label="Subtítulo"
              value={p.subtitulo ?? ''}
              onChange={(e) => setPonente(i, { subtitulo: e.target.value || null })}
              hint="Cargo o profesión, entre el nombre y la bio (opcional)"
            />
            <TextField
              label="Foto (URL)"
              value={p.fotoUrl ?? ''}
              onChange={(e) => setPonente(i, { fotoUrl: e.target.value || null })}
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-texto">Bio corta</label>
              <textarea
                className="min-h-20 w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto shadow-sm focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
                value={p.descripcion ?? ''}
                onChange={(e) => setPonente(i, { descripcion: e.target.value || null })}
              />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function FooterConfig({
  f,
  set,
}: {
  f: Evento;
  set: <K extends keyof Evento>(k: K, v: Evento[K]) => void;
}) {
  const footer = f.footer ?? FOOTER_VACIO;

  function setFooter(patch: Partial<FooterInvitacion>) {
    set('footer', { ...footer, ...patch });
  }

  function setDetalle(i: number, patch: Partial<DetalleFooter>) {
    setFooter({ detalles: footer.detalles.map((d, idx) => (idx === i ? { ...d, ...patch } : d)) });
  }

  function agregarDetalle() {
    setFooter({ detalles: [...footer.detalles, { etiqueta: '', valor: '' }] });
  }

  function quitarDetalle(i: number) {
    setFooter({ detalles: footer.detalles.filter((_, idx) => idx !== i) });
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-texto">Footer</h2>
        <p className="mt-1 text-xs text-texto/50">
          Bloque final de la landing (p.ej. redes sociales y ficha del libro). Deja todo en blanco para
          no mostrar el footer.
        </p>
      </div>
      <TextField
        label="Título"
        value={footer.titulo ?? ''}
        onChange={(e) => setFooter({ titulo: e.target.value || null })}
        hint='Por ejemplo: "Sigue la conversación"'
      />
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-texto">Descripción</label>
        <textarea
          className="min-h-20 w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto shadow-sm focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
          value={footer.descripcion ?? ''}
          onChange={(e) => setFooter({ descripcion: e.target.value || null })}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Facebook (URL)"
          value={footer.facebookUrl ?? ''}
          onChange={(e) => setFooter({ facebookUrl: e.target.value || null })}
        />
        <TextField
          label="Instagram (URL)"
          value={footer.instagramUrl ?? ''}
          onChange={(e) => setFooter({ instagramUrl: e.target.value || null })}
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-texto">Detalles (p.ej. ficha del libro)</label>
        <p className="text-xs text-texto/50">
          Pares etiqueta/valor libres — p.ej. Título / Renacer de las cenizas, Autor / Estiben Handal D.
        </p>
        <div className="space-y-2">
          {footer.detalles.map((d, i) => (
            <div key={i} className="flex gap-2">
              <input
                type="text"
                className="w-2/5 rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
                placeholder="Etiqueta (Título)"
                value={d.etiqueta}
                onChange={(e) => setDetalle(i, { etiqueta: e.target.value })}
              />
              <input
                type="text"
                className="flex-1 rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
                placeholder="Valor (Renacer de las cenizas)"
                value={d.valor}
                onChange={(e) => setDetalle(i, { valor: e.target.value })}
              />
              <Button type="button" variante="peligro" onClick={() => quitarDetalle(i)}>
                ×
              </Button>
            </div>
          ))}
          {footer.detalles.length === 0 && (
            <span className="text-xs text-texto/40">Sin detalles todavía.</span>
          )}
        </div>
        <Button type="button" variante="secundario" onClick={agregarDetalle}>
          Agregar detalle
        </Button>
      </div>
    </div>
  );
}

function ColorField({
  etiqueta,
  valor,
  onChange,
}: {
  etiqueta: string;
  valor: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-texto">{etiqueta}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-borde bg-white p-1"
          value={valor ?? '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          aria-label={etiqueta}
        />
        <input
          type="text"
          className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
          placeholder="#000000"
          value={valor ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}
